import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft, ChevronRight, UserRound, ShieldCheck, Clock, ReceiptText, CalendarClock,
  Palette, Mail, Wallet, Globe, LogOut, Sparkles, Gift, Gem, Award, Trophy, Users2,
  LifeBuoy, HelpCircle, Compass, Crown,
} from "lucide-react";

function Row({ to, icon: Icon, label, sub, testId }) {
  return (
    <Link to={to} data-testid={testId} className="group flex items-center gap-3 border-b border-border px-4 py-3.5 last:border-b-0 hover:bg-accent/40">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Icon className="h-4.5 w-4.5" /></div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{label}</p>
        {sub && <p className="truncate text-xs text-muted-foreground">{sub}</p>}
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

function Group({ title, children }) {
  return (
    <div>
      <p className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      <div className="overflow-hidden rounded-2xl border border-border bg-card">{children}</div>
    </div>
  );
}

export default function CoachSettings() {
  const { user, logout } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  usePageMeta(t("cset.meta_title"));

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  return (
    <div data-testid="coach-settings-page">
      <Seo title={t("cset.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="cset-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.back_dashboard")}
      </Link>
      <PageHeader eyebrow={t("cset.eyebrow")} title={t("cset.title")} subtitle={t("cset.subtitle")} />

      <div className="space-y-5">
        <Group title={t("cset.group_account")}>
          <Row to="/app/coach/profile" icon={UserRound} label={t("cset.row_profile")} sub={t("cset.row_profile_sub")} testId="cset-row-profile" />
          <Row to="/app/coach/professional" icon={Award} label={t("cset.row_professional")} sub={t("cset.row_professional_sub")} testId="cset-row-professional" />
          <Row to="/app/coach/verify" icon={ShieldCheck} label={t("cset.row_verify")} sub={t("cset.row_verify_sub")} testId="cset-row-verify" />
        </Group>

        <Group title={t("cset.group_bookings")}>
          <Row to="/app/coach/availability" icon={Clock} label={t("cset.row_availability")} sub={t("cset.row_availability_sub")} testId="cset-row-availability" />
          <Row to="/app/coach/settings/refund-policy" icon={ReceiptText} label={t("cset.row_refund")} sub={t("cset.row_refund_sub")} testId="cset-row-refund" />
          <Row to="/app/coach/settings/reschedule" icon={CalendarClock} label={t("cset.row_reschedule")} sub={t("cset.row_reschedule_sub")} testId="cset-row-reschedule" />
          <Row to="/app/coach/settings/check-in" icon={Sparkles} label={t("cset.row_checkin")} sub={t("cset.row_checkin_sub")} testId="cset-row-checkin" />
        </Group>

        <Group title={t("cset.group_growth")}>
          <Row to="/app/coach/referral" icon={Gift} label={t("cset.row_referral")} sub={t("cset.row_referral_sub")} testId="cset-row-referral" />
          <Row to="/app/coach/inner-circle-invites" icon={Gem} label={t("cset.row_ic_invites")} sub={t("cset.row_ic_invites_sub")} testId="cset-row-ic-invites" />
          <Row to="/app/coach/client-wins" icon={Trophy} label={t("cset.row_wins")} sub={t("cset.row_wins_sub")} testId="cset-row-wins" />
          <Row to="/app/coach/collectives" icon={Users2} label={t("cset.row_collectives")} sub={t("cset.row_collectives_sub")} testId="cset-row-collectives" />
        </Group>

        <Group title={t("cset.group_brand")}>
          <Row to="/app/coach/settings/branding" icon={Palette} label={t("cset.row_branding")} sub={t("cset.row_branding_sub")} testId="cset-row-branding" />
          <Row to="/app/coach/settings/weekly-digest" icon={Mail} label={t("cset.row_digest")} sub={t("cset.row_digest_sub")} testId="cset-row-digest" />
        </Group>

        <Group title={t("cset.group_billing")}>
          <Row to="/app/coach/upgrade" icon={Crown} label={t("cset.row_upgrade")} sub={t("cset.row_upgrade_sub")} testId="cset-row-upgrade" />
          <Row to="/app/coach/settings/credits" icon={Wallet} label={t("cset.row_credits", { n: user?.credits ?? 0 })} sub={t("cset.row_credits_sub")} testId="cset-row-credits" />
        </Group>

        <Group title={t("cset.group_support")}>
          <Row to="/app/coach/support" icon={LifeBuoy} label={t("cset.row_support")} sub={t("cset.row_support_sub")} testId="cset-row-support" />
          <Row to="/app/coach/help" icon={HelpCircle} label={t("cset.row_help")} sub={t("cset.row_help_sub")} testId="cset-row-help" />
          <Row to="/app/coach/tour" icon={Compass} label={t("cset.row_tour")} sub={t("cset.row_tour_sub")} testId="cset-row-tour" />
        </Group>

        <Group title={t("cset.group_language")}>
          <div className="flex items-center justify-between gap-3 px-4 py-3.5">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Globe className="h-4.5 w-4.5" /></div>
              <p className="text-sm font-medium text-foreground">{t("cset.row_language")}</p>
            </div>
            <LanguageSwitcher />
          </div>
        </Group>

        <Button variant="outline" className="w-full gap-2 rounded-full text-rose-600 hover:text-rose-700" onClick={async () => { await logout(); navigate("/"); }} data-testid="cset-logout">
          <LogOut className="h-4 w-4" /> {t("cset.logout")}
        </Button>
      </div>
    </div>
  );
}
